import {
  DefaultCreativeManager,
  CreativeManager
} from 'core/creative/CreativeManager';
import {
  CreativeOverviewState,
  BindCreativeState,
  CreativeSummaryState,
  ModifyCreativeActivationState
} from './CreativeManagementState';
import {
  ModalStateFactory,
  ModalState
} from 'containers/Common/ModalStateFactory';
import { CreativeOfCampaign } from 'core/creative/Creative';
import { AddonFeatureManager } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import * as H from 'history';
import { DefaultBindCreativeStateContentModel } from './BindCreativeStateContentModel';
import { DefaultCreativeSummaryStateContentModel } from './CreativeSummaryStateContentModel';
import { DefaultModifyCreativeActivationStateContentModel } from './ModifyCreativeActivationStateContentModel';
import { DefaultCreativeOverviewStateContentModel } from './CreativeOverviewStateContentModel';
import {
  CreativeBindData,
  CreativeManagementData
} from 'core/rtbCampaign/CreativeBindData';
import { L1Object } from 'core/l1Object/L1Object';
import _ from 'lodash';
import { CreativeManagementStateData } from './CreativeManagementStateContext';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';

export enum CreativeManagementAction {
  BIND = 'bind',
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate',
  MANAGE = 'manage'
}
export class DefaultCreativeManagementStateFactory implements ModalStateFactory<CreativeManagementStateData> {
  channel: string;

  constructor(
    protected campaigns: CreativeBindData[],
    protected advertiserId: number,
    protected editAction: string,
    protected addonFeatureManager: AddonFeatureManager,
    protected orderNumber: string,
    protected history: H.History,
    protected l1Object: L1Object,
    protected manager: CreativeManager = new DefaultCreativeManager()
  ) {
    this.channel = _.get(l1Object, 'channel', ChannelGroupSetting.RTB.key);
    if (this.campaigns.length >= 1 && this.editAction === 'manage') {
      this.campaigns = [this.campaigns[0]];
    }
  }

  async initStates(
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean) => void
  ) {
    const creativeData = await this.getCreativeData();
    switch (this.editAction) {
      case CreativeManagementAction.BIND:
        return this.initBindStates(creativeData, goModalStateFn, closeModalFn);
      case CreativeManagementAction.ACTIVATE:
        return this.initModifyActivationStates(
          creativeData,
          goModalStateFn,
          closeModalFn,
          true
        );
      case CreativeManagementAction.DEACTIVATE:
        return this.initModifyActivationStates(
          creativeData,
          goModalStateFn,
          closeModalFn,
          false
        );
      default:
        return this.initManageStates(
          creativeData,
          goModalStateFn,
          closeModalFn
        );
    }
  }

  async getCreativeData(): Promise<CreativeManagementData[]> {
    let creativeData: CreativeManagementData[] = [];
    try {
      await Promise.all(
        this.campaigns.map(async (campaign) => {
          try {
            const creatives = await this.getCreatives(
              campaign.id,
              campaign.draftId
            );
            creativeData.push({
              id: campaign.id,
              name: campaign.name,
              startDate: campaign.startDate,
              endDate: campaign.endDate,
              creatives: creatives,
              draftId: campaign.draftId,
              channel: campaign.channel,
              channelGroupId: campaign.channelGroupId,
              storeOverBindingLimitCount: campaign.storeOverBindingLimitCount
            });
          } catch (e) {
            creativeData.push({
              id: campaign.id,
              name: campaign.name,
              startDate: campaign.startDate,
              endDate: campaign.endDate,
              creatives: [],
              channel: campaign.channel,
              channelGroupId: campaign.channelGroupId,
              storeOverBindingLimitCount: campaign.storeOverBindingLimitCount
            });
          }
        })
      );
    } catch (error) {
      console.log('cannot get creative datas of campaigns', error);
    }
    creativeData = creativeData.sort((campaign1, campaign2) => {
      return campaign2.id - campaign1.id;
    });
    return creativeData;
  }

  async getCreatives(l2ChannelId: number, draftId?: number) {
    if (draftId) {
      return this.manager.getCreativesByCampaignId(draftId, true);
    }
    return this.manager.getCreativesByCampaignId(l2ChannelId, false);
  }

  getOverviewStateContentModel(
    creatives: CreativeOfCampaign[],
    l3ObjectStateFn: () => void,
    refreshCreative: () => Promise<CreativeOfCampaign[]>
  ) {
    return new DefaultCreativeOverviewStateContentModel(
      this.l1Object,
      this.campaigns[0],
      creatives,
      this.addonFeatureManager.isFeatureEnable(
        ADDONFEATURE.REPORT.REPORT_CLOSINGREPORT
      ),
      this.orderNumber,
      l3ObjectStateFn,
      this.goCreateCreativeFn,
      refreshCreative
    );
  }

  getBindCreativeStateContentModel(
    forbidCreatives: number[],
    hasActiveCreative = false
  ) {
    return new DefaultBindCreativeStateContentModel(
      this.campaigns,
      this.advertiserId,
      forbidCreatives,
      false,
      this.manager,
      hasActiveCreative
    );
  }

  getModifyCreativeActivationStateContentModel(
    creativeData: CreativeManagementData[],
    activate: boolean
  ) {
    return new DefaultModifyCreativeActivationStateContentModel(
      creativeData,
      activate,
      this.l1Object
    );
  }

  getCreativeSummaryStateContentModel(creativeData: CreativeManagementData[]) {
    return new DefaultCreativeSummaryStateContentModel(
      creativeData,
      this.l1Object.channel,
      this.l1Object.channelGroupId
    );
  }

  async initManageStates(
    creativeData: CreativeManagementData[],
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean) => void
  ) {
    const refreshCreative = async () => {
      const newCreativeData = await this.getCreativeData();
      return newCreativeData[0].creatives;
    };
    const creatives: CreativeOfCampaign[] = creativeData[0].creatives;
    const bindCreativeState = new BindCreativeState(
      this.campaigns,
      this.getBindCreativeStateContentModel(
        creatives.map((creative) => creative.id),
        creatives.some((creative) => creative.isActiveBinding)
      )
    );
    const creativeOverviewState = new CreativeOverviewState(
      this.campaigns,
      this.getOverviewStateContentModel(
        creatives,
        () => {
          bindCreativeState.contentModel.reset();
          goModalStateFn(bindCreativeState);
        },
        refreshCreative
      )
    );

    const creativeSummaryState = new CreativeSummaryState(
      this.campaigns,
      this.getCreativeSummaryStateContentModel(creativeData)
    );

    creativeOverviewState.setCloseFn(closeModalFn);
    bindCreativeState.setNextFn(() => goModalStateFn(creativeSummaryState));
    bindCreativeState.setBackFn(() => goModalStateFn(creativeOverviewState));
    creativeSummaryState.setBackFn(() => goModalStateFn(bindCreativeState));
    creativeSummaryState.setCloseFn(closeModalFn);
    return creativeOverviewState;
  }

  async initBindStates(
    creativeData: CreativeManagementData[],
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean) => void
  ) {
    const forbidCreatives =
      creativeData.length === 1
        ? creativeData[0].creatives.map((creative) => creative.id)
        : [];
    // 複選活動時只要有一個活動已經有啟用中的素材就整個擋掉：綁定是綁進所有選到的活動，
    // 放行等於讓那個活動變成兩份啟用中。要放過其他活動就自己少選幾個再綁。
    const hasActiveCreative = creativeData.some((data) =>
      data.creatives.some((creative) => creative.isActiveBinding)
    );
    let bindCreativeState = new BindCreativeState(
      this.campaigns,
      this.getBindCreativeStateContentModel(forbidCreatives, hasActiveCreative)
    );
    const creativeSummaryState = new CreativeSummaryState(
      this.campaigns,
      this.getCreativeSummaryStateContentModel(creativeData)
    );
    bindCreativeState.setNextFn(() => goModalStateFn(creativeSummaryState));
    bindCreativeState.setCloseFn(closeModalFn);
    creativeSummaryState.setBackFn(() => goModalStateFn(bindCreativeState));
    creativeSummaryState.setCloseFn(closeModalFn);
    return bindCreativeState;
  }

  async initModifyActivationStates(
    creativeData: CreativeManagementData[],
    goModalStateFn: (
      state: ModalState,
      beforeGoState?: () => Promise<void>
    ) => void,
    closeModalFn: (dirty: boolean) => void,
    activate: boolean
  ) {
    let modifyCreativeActivationState = new ModifyCreativeActivationState(
      this.campaigns,
      this.getModifyCreativeActivationStateContentModel(creativeData, activate)
    );
    const creativeSummaryState = new CreativeSummaryState(
      this.campaigns,
      this.getCreativeSummaryStateContentModel(creativeData)
    );

    modifyCreativeActivationState.setNextFn(() =>
      goModalStateFn(creativeSummaryState)
    );
    modifyCreativeActivationState.setCloseFn(closeModalFn);
    creativeSummaryState.setBackFn(() =>
      goModalStateFn(modifyCreativeActivationState)
    );
    creativeSummaryState.setCloseFn(closeModalFn);
    return modifyCreativeActivationState;
  }

  goCreateCreativeFn = () => {
    const campaignParams = this.campaigns[0].draftId
      ? `draftId=${this.campaigns[0].draftId}`
      : `campaignId=${this.campaigns[0].id}`;
    let path = `/creatives/new?${campaignParams}&orderNumber=${this.orderNumber}`;
    if (this.l1Object) {
      path = `${path}&l1ObjectId=${this.l1Object.l1ObjectId}&channel=${this.channel}`;
    }
    this.history.push(path);
  };
}
